import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Label,
    Spinner
} from "reactstrap";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import { AvField, AvForm } from "availity-reactstrap-validation";
import IndustriesService from "src/modules/3rd-party-management/apis/common/IndustriesService";
import Select from "react-select";
import {
    useEffect,
    useMemo,
    useState
} from "react";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";

const filterOptions = (
    candidate,
    input
) => {
    if (input) {
        const candidateInput = candidate.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");
        const normalizedInput = input.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

        return candidateInput.indexOf(normalizedInput) >= 0 || ((candidate.data.keywords || []).findIndex((keyword) => {
            const normalizedKeyword = keyword.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

            return normalizedKeyword.indexOf(normalizedInput) >= 0
        }) >= 0);
    }

    return true;
};

const NewProductModal = ({
    t,
    lng,
    supplierId,
    industryIds,
    show,
    toggle,
    prevData,
    onSuccess
}) => {
    const [selectedProduct, setSelectedProduct] = useState();
    const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);
    const queryClient = useQueryClient();

    const refreshProductDocuments = () => {
        queryClient.invalidateQueries({
            queryKey: ['3rd-party-management-fetch-documents-management-list'],
        });
    };

    const productSaveErrorMessage = (error, fallback) => {
        const message = error?.response?.data?.error;

        return typeof message === 'string' && message ? message : t(fallback);
    };

    const hasEnitityFeature = isFeatureEnabledForActiveModule('entity_management');

    const {
        data: products,
        isFetching: productsAreFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-industry-products-list',
            supplierId,
            industryIds
        ],
        queryFn: async () => {
            const service = IndustriesService.getInstance();

            return await service.fetchProducts({ selectedIndustryIds: industryIds }, lng);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        },
    });

    const {
        data: legalEntitiesData,
        isFetching: legalEntitiesListIsFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-legal-entities-list',
            'creditor'
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.fetchLegalEntities();
        },
        enabled: hasEnitityFeature,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching entities.'), {
                type: 'error'
            });
        }
    });

    const productsList = useMemo(() => {
        if (Array.isArray(products)) {
            return products.map((product) => {
                return {
                    value: product.id,
                    label: `${product.class} - ${t(product.name)}`,
                    baseLabel: product.name
                }
            });
        }

        return [];
    }, [products, t]);

    const entitiesOptions = useMemo(() => {
        return (legalEntitiesData?.legalEntities || []).map((legal) => {
                return {
                    value: legal.id,
                    label: legal.title,
                    shortName: legal.shortName,
                    naceCode: legal.nace_code
                }
            }) 
    }, [ legalEntitiesData ])

    const {
        mutate: createProductMutation,
        isLoading: creationIsInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.create(payload);
        },
        onSuccess: () => {
            refreshProductDocuments();
            onSuccess();
            toggle();

            toast(t("New product created successfully."), {
                type: "success"
            });
        },
        onError: (error) => {
            toast(productSaveErrorMessage(error, "An error occurred while creating product."), {
                type: "error"
            });
        }
    });

    const {
        mutate: updateProductMutation,
        isLoading: updateIsInProcess
    } = useMutation({
        mutationFn: async ({
            id,
            payload
        }) => {
            const service = SupplierProductService.getInstance();
            return await service.update(id, payload);
        },
        onSuccess: () => {
            refreshProductDocuments();
            onSuccess();
            toggle();

            toast(t("product updated successfully."), {
                type: "success"
            });
        },
        onError: (error) => {
            toast(productSaveErrorMessage(error, "An error occurred while updating product."), {
                type: "error"
            });
        }
    });

    useEffect(() => {
        if(show) {
            if(prevData) { 
                setSelectedProduct({
                    value: prevData.name?.id,
                    label: `${prevData.name?.class} - ${t(prevData.name?.name)}`,
                    baseLabel: prevData.name?.name
                });

                setSelectedLegalEntities((prevData.legal_entities || []).map((l) => {
                    return {
                        value: l.id,
                        label: l.name
                    }
                }));
            }
        }
        else {
            setSelectedProduct(null);
            setSelectedLegalEntities([]);
        }
    }, [ 
        show,
        prevData,
        t
    ]);

    return (
        <Modal 
            size="xl" 
            isOpen={show}
            role="dialog"
            aria-labelledby="createProductTitle"
            aria-modal="true"
        >
            <AvForm
                className="needs-validation m-2"
                onValidSubmit={(_e, values) => {
                    const payload = {
                        ...values,
                        articleNumber: values.articleNumber ?? '',
                        internalSku: values.internalSku ?? '',
                    };

                    if(prevData) {
                        updateProductMutation({
                            payload,
                            id: prevData.id
                        })
                    }
                    else { 
                        createProductMutation({
                            ...payload,
                            supplier: supplierId
                        });
                    }
                }}
            >
                <ModalHeader 
                    className="border-0" 
                    toggle={toggle}
                    id="createProductTitle"
                >
                    {t(prevData ? "Edit Product" : "Add New Product")}
                </ModalHeader>

                <ModalBody>
                    <Row>
                        <Col sm='6' className="mb-3">
                            <Label>
                                {t('NACE Code')}
                            </Label>
                            <Select
                                isLoading={productsAreFetching}
                                isDisabled={productsAreFetching}
                                placeholder={t("NACE Code")}
                                classNamePrefix="select2-selection"
                                options={productsList}
                                onChange={setSelectedProduct}
                                value={selectedProduct}
                            />
                            <AvField
                                type="hidden"
                                name="name"
                                validate={{
                                    required: { value: true }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={selectedProduct?.value}
                            />
                        </Col>

                        <Col sm='6' className="mb-3">
                            <Label>
                                {t('Product Name')}
                            </Label>
                            <AvField
                                type="text"
                                name="productName"
                                validate={{
                                    required: { value: false }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={prevData?.productName}
                            />
                        </Col>

                        <Col sm='6' className="mb-3">
                            <Label htmlFor="articleNumber">
                                {t('Article Number')}
                            </Label>
                            <AvField
                                id="articleNumber"
                                name="articleNumber"
                                type="text"
                                errorMessage={t('This value must be 255 characters or fewer.')}
                                validate={{
                                    maxLength: { value: 255 }
                                }}
                                value={prevData?.articleNumber ?? prevData?.article_number ?? ''}
                            />
                        </Col>

                        <Col sm='6' className="mb-3">
                            <Label htmlFor="internalSku">
                                {t('Internal SKU')}
                            </Label>
                            <AvField
                                id="internalSku"
                                name="internalSku"
                                type="text"
                                errorMessage={t('This value must be 255 characters or fewer.')}
                                validate={{
                                    maxLength: { value: 255 }
                                }}
                                value={prevData?.internalSku ?? prevData?.internal_sku ?? ''}
                            />
                        </Col>

                        {hasEnitityFeature && (
                            <Col sm='6' className="mb-3">
                                <Label for="legal-entities">
                                    {t("Legal Entities")}:
                                </Label>

                                <Select
                                    isMulti
                                    placeholder={t("Select") + "..."}
                                    classNamePrefix="select2-selection"
                                    id="legal-entities"
                                    options={entitiesOptions}
                                    onChange={(e) => {
                                        setSelectedLegalEntities(e)
                                    }}
                                    value={selectedLegalEntities}
                                    filterOption={filterOptions}
                                    isDisabled={legalEntitiesListIsFetching}
                                    isLoading={legalEntitiesListIsFetching}
                                />
                                <AvField
                                    type="hidden"
                                    name="legal_entity_ids"
                                    validate={{
                                        required: { value: false }
                                    }}
                                    errorMessage={t("This field cannot be blank")}
                                    value={(selectedLegalEntities || []).map((l) => l.value)}
                                />
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col sm='12' md='12' className="mb-3">
                            <Label htmlFor="description">
                                {t('Description')}
                            </Label>
                            <AvField
                                id="description"
                                name="description"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={prevData?.description}
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button 
                        color="danger" 
                        onClick={toggle}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button 
                        type="submit" 
                        color="primary"
                        disabled={creationIsInProcess || updateIsInProcess}
                    >
                        {(creationIsInProcess || updateIsInProcess) && (
                            <Spinner 
                                size='sm'
                                color='white'
                                className="me-1"
                            />
                        )}

                        {t('Save Product')}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    )
};

export default withNamespaces()(NewProductModal);